import { useEChart } from './useEChart.js';
import { ChartFrame } from './ChartFrame.js';
import { formatChartAmount } from './formatters.js';

import type { ChartBaseStateProps } from './types.js';

export interface CashflowPeriod {
  readonly period: string;
  readonly inflowRial: number;
  readonly outflowRial: number;
}

export interface CashflowChartProps extends ChartBaseStateProps {
  readonly periods: readonly CashflowPeriod[];
  readonly ariaLabel: string;
  readonly height?: number | undefined;
}

/**
 * Real posted treasury transactions only (kind 'receipt' → inflow, kind
 * 'payment' → outflow — see TreasuryTransaction in
 * apps/client/src/lib/types/finance.ts), summed by the caller's own
 * period grouping — the same real-amount-summing this codebase already
 * does for monthly totals elsewhere (e.g. ProcurementHubPage), not a
 * forecast or a backend rule. A real cashflow *forecast* would need a
 * backend rule and does not exist; this chart never claims to be one.
 * Green/red for in/out is a directional convention (money in is good),
 * not the categorical series order.
 */
export function CashflowChart({
  periods,
  ariaLabel,
  height = 260,
  isLoading,
  loadingMessage,
  error,
  status,
  errorMessage,
  onRetry,
  retryLabel,
  emptyMessage,
  amountUnit = 'rial',
}: CashflowChartProps) {
  const hasError = Boolean(error || (status && status >= 400));
  const isEmpty =
    !isLoading &&
    !hasError &&
    (periods.length === 0 || periods.every((p) => p.inflowRial === 0 && p.outflowRial === 0));
  const containerRef = useEChart(
    (theme) => ({
      grid: { left: 8, right: 12, top: 32, bottom: 28, containLabel: true },
      legend: {
        top: 0,
        textStyle: { color: theme.text, fontSize: 12 },
        icon: 'roundRect',
        itemWidth: 10,
        itemHeight: 10,
      },
      xAxis: {
        type: 'category',
        data: periods.map((p) => p.period),
        axisLine: { lineStyle: { color: theme.grid } },
        axisTick: { show: false },
        axisLabel: { color: theme.textMuted, fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: theme.grid } },
        axisLabel: {
          color: theme.textMuted,
          fontSize: 11,
          formatter: (v: number) =>
            formatChartAmount(v, { unit: amountUnit, includeSuffix: false }),
        },
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: number) =>
          formatChartAmount(v, { unit: amountUnit, includeSuffix: true }),
        axisPointer: { type: 'shadow' },
      },
      series: [
        {
          type: 'bar',
          name: 'ورودی',
          data: periods.map((p) => p.inflowRial),
          barMaxWidth: 20,
          itemStyle: { color: theme.success, borderRadius: [4, 4, 0, 0] },
        },
        {
          type: 'bar',
          name: 'خروجی',
          data: periods.map((p) => p.outflowRial),
          barMaxWidth: 20,
          itemStyle: { color: theme.danger, borderRadius: [4, 4, 0, 0] },
        },
      ],
    }),
    [periods, amountUnit],
  );

  return (
    <ChartFrame
      containerRef={containerRef}
      height={height}
      ariaLabel={ariaLabel}
      isLoading={isLoading}
      loadingMessage={loadingMessage}
      error={error}
      status={status}
      errorMessage={errorMessage}
      onRetry={onRetry}
      retryLabel={retryLabel}
      isEmpty={isEmpty}
      emptyMessage={emptyMessage}
    />
  );
}
